import {Helmet} from '@dr.pogodin/react-helmet';
import {openDeleteDialog, showNotification} from 'platform/components';
import {match} from 'ts-pattern';

import {isArray} from 'ramda-adjunct';

import {useDeleteDispensingUnitPresetsMutation} from '@dms/api/warehouse';
import i18n from '@dms/i18n';
import {settingsRoutes, testIds} from '@dms/routes';
import {SettingsTemplate, SettingsTemplateHeader} from '@dms/settings/shared';
import {
  catchUnhandledDataGridActions,
  DmsDataGrid,
  handleApiError,
  usePermissions,
} from '@dms/shared';

import {composePath, useNavigate} from 'shared/web';

import type {ActionCallback} from 'features/eag-grid';

export function WarehousesDispensingUnitsPresets() {
  const navigate = useNavigate();

  const [canCreateWarehouseDictionary] = usePermissions({
    permissionKeys: ['warehouseDictionaryCreate'],
  });

  const [deleteDispensingUnitPresets] = useDeleteDispensingUnitPresetsMutation();

  const actionCallback: ActionCallback = ({actionKey, rowId, refreshData, deselectAll}) => {
    match(actionKey)
      .with('redirectDetail', () => {
        const id = isArray(rowId) ? rowId[0] : rowId;
        navigate(
          composePath(settingsRoutes.warehousesDispensingUnitsPresetsDetail, {params: {id}})
        );
      })
      .with('delete', () => {
        const ids = isArray(rowId) ? rowId : [rowId];
        openDeleteDialog({
          onConfirm: () =>
            deleteDispensingUnitPresets({codelistIds: ids})
              .unwrap()
              .then(() =>
                showNotification.success(i18n.t('general.notifications.successfullyDeleted'))
              )
              .then(() => refreshData())
              .then(deselectAll)
              .catch(handleApiError),
        });
      })
      .otherwise(() => catchUnhandledDataGridActions(actionKey));
  };

  const header: SettingsTemplateHeader = {
    title: i18n.t('entity.warehouse.labels.dispensingUnitsPresets'),
    actions: [
      {
        type: 'button',
        title: i18n.t('entity.warehouse.actions.newDispensingUnitPreset'),
        onClick: () => navigate(settingsRoutes.warehousesDispensingUnitsPresetsCreate),
        isDisabled: !canCreateWarehouseDictionary,
        tooltip: !canCreateWarehouseDictionary
          ? i18n.t('module.warehouse.label.missingWarehouseDictionaryCreatePermission')
          : undefined,
      },
    ],
  };

  return (
    <>
      <Helmet title={i18n.t('entity.warehouse.labels.dispensingUnitsPresets')} />
      <SettingsTemplate
        data-testid={testIds.settings.warehousesDispensingUnitsPresets('template')}
        header={header}
      >
        <DmsDataGrid
          gridCode="warehouse-article-dispensing-unit-preset"
          actionCallback={actionCallback}
        />
      </SettingsTemplate>
    </>
  );
}
